//@flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import getter from 'dibs-object-getter';

import Home from 'dibs-icons/exports/Home';
import Gallery from 'dibs-icons/exports/Gallery';
import Heart from 'dibs-icons/exports/HeartOutlined';

import { Tabs } from 'dibs-elements/exports/Tabs';
import { PAGE_TYPE_FAVORITES, PAGE_TYPE_PROJECTS, PAGE_TYPE_OVERVIEW } from '../constants';

import type { PageType } from '../constants';
import type { ProfileTabs_tradeFirm as TradeFirmType } from './__generated__/ProfileTabs_tradeFirm.graphql';

import styles from './ProfileTabs.scss';

type ProfileTabProps = {
    isEditMode: boolean,
    onClick: (pageType: PageType) => void,
    count?: number,
    icon?: Element,
    type: PageType,
    label: Element,
};

export const PAGE_TYPE_TAB_ORDER = [PAGE_TYPE_OVERVIEW, PAGE_TYPE_PROJECTS, PAGE_TYPE_FAVORITES];

export const ProfileTab = (props: ProfileTabProps) =>
    !props.isEditMode && !props.count && props.type !== PAGE_TYPE_OVERVIEW ? null : (
        <div
            onClick={() => {
                if (props.onClick) {
                    props.onClick(props.type);
                }
            }}
            data-tn={`profile-tab-${props.type}`}
            className={styles.tabWrapper}
        >
            {props.count ? <div className={styles.tabCount}>{props.count}</div> : props.icon}
            <div className={styles.tabLabel}>{props.label}</div>
        </div>
    );

type Props = {
    onChangeTab: (pageType: PageType) => void,
    currentTab: PageType,
    isEditMode: boolean,
    tradeFirm: ?TradeFirmType,
};

export const ProfileTabsComponent = (props: Props) => {
    const { isEditMode, currentTab, onChangeTab, tradeFirm } = props;
    const projectCount =
        getter(tradeFirm)
            .at('publicProfile')
            .value('designProjectsCount') || 0;
    const favoriteItemCount =
        getter(tradeFirm)
            .at('publicProfile')
            .at('portfolios')
            .at(0)
            .value('itemCount') || 0;
    return (
        <div className={`colXl5 colXs12 ${styles.wrapper}`}>
            <Tabs activeTabIndex={PAGE_TYPE_TAB_ORDER.indexOf(currentTab)} collapsed>
                <ProfileTab
                    isEditMode={isEditMode}
                    type={PAGE_TYPE_OVERVIEW}
                    onClick={onChangeTab}
                    label={
                        <FormattedMessage
                            id="abt.designProfile.header.tab.overview"
                            defaultMessage="Profile"
                        />
                    }
                    icon={<Home className={styles.tabIcon} />}
                />
                <ProfileTab
                    isEditMode={isEditMode}
                    type={PAGE_TYPE_PROJECTS}
                    onClick={onChangeTab}
                    label={
                        <FormattedMessage
                            id="abt.designProfile.header.tab.projects"
                            defaultMessage="Projects"
                        />
                    }
                    icon={<Gallery className={styles.tabIcon} />}
                    count={projectCount}
                />
                <ProfileTab
                    isEditMode={isEditMode}
                    type={PAGE_TYPE_FAVORITES}
                    onClick={onChangeTab}
                    label={
                        <FormattedMessage
                            id="abt.designProfile.header.tab.favorites"
                            defaultMessage="Favorites"
                        />
                    }
                    icon={<Heart className={styles.tabIcon} />}
                    count={favoriteItemCount}
                />
            </Tabs>
        </div>
    );
};

export const ProfileTabs = createFragmentContainer(ProfileTabsComponent, {
    tradeFirm: graphql`
        fragment ProfileTabs_tradeFirm on TradeFirmType {
            publicProfile {
                designProjectsCount
                portfolios {
                    itemCount
                }
            }
        }
    `,
});
